import type { PermissionAccess, TeamCaptainAssignmentRecord, TeamPermissionFeature, TeamPermissionPreset } from '../types';

export const permissionAccessLevels: PermissionAccess[] = ['none', 'view', 'edit'];

export const teamPermissionFeatures: { key: TeamPermissionFeature; label: string; editable: boolean }[] = [
  { key: 'planning_board', label: 'Planning Board', editable: true },
  { key: 'season_planning', label: 'Season Planning', editable: true },
  { key: 'matches_lineups', label: 'Matches & Lineups', editable: true },
  { key: 'team_events', label: 'Team Events', editable: true },
  { key: 'player_availability', label: 'Player Availability', editable: true },
  { key: 'fees_payments', label: 'Fees & Payments', editable: true },
  { key: 'communications', label: 'Communications', editable: true },
  { key: 'reports', label: 'Reports', editable: false },
  { key: 'roster_management', label: 'Team / Roster Management', editable: true },
  { key: 'partner_preferences', label: 'Partner Preferences', editable: true },
  { key: 'users_permissions', label: 'Users & Permissions', editable: true },
];

export const teamPermissionPresets: { key: TeamPermissionPreset; label: string }[] = [
  { key: 'admin', label: 'Admin' },
  { key: 'captain', label: 'Captain' },
  { key: 'co_captain', label: 'Co-Captain' },
  { key: 'lineup_manager', label: 'Lineup Manager' },
  { key: 'team_coordinator', label: 'Team Coordinator' },
  { key: 'viewer', label: 'Viewer' },
  { key: 'player', label: 'Player' },
  { key: 'custom', label: 'Custom' },
];

export type TeamPermissionMatrix = Record<TeamPermissionFeature, PermissionAccess>;

const admin: TeamPermissionMatrix = {
  planning_board: 'edit',
  season_planning: 'edit',
  matches_lineups: 'edit',
  team_events: 'edit',
  player_availability: 'edit',
  fees_payments: 'edit',
  communications: 'edit',
  reports: 'view',
  roster_management: 'edit',
  partner_preferences: 'edit',
  users_permissions: 'edit',
};

const captain: TeamPermissionMatrix = {
  planning_board: 'edit',
  season_planning: 'edit',
  matches_lineups: 'edit',
  team_events: 'edit',
  player_availability: 'edit',
  fees_payments: 'edit',
  communications: 'edit',
  reports: 'view',
  roster_management: 'edit',
  partner_preferences: 'edit',
  users_permissions: 'view',
};

const coCaptain: TeamPermissionMatrix = {
  planning_board: 'edit',
  season_planning: 'edit',
  matches_lineups: 'edit',
  team_events: 'edit',
  player_availability: 'edit',
  fees_payments: 'view',
  communications: 'edit',
  reports: 'view',
  roster_management: 'edit',
  partner_preferences: 'view',
  users_permissions: 'none',
};

const lineupManager: TeamPermissionMatrix = {
  planning_board: 'edit',
  season_planning: 'edit',
  matches_lineups: 'edit',
  team_events: 'view',
  player_availability: 'edit',
  fees_payments: 'view',
  communications: 'view',
  reports: 'view',
  roster_management: 'view',
  partner_preferences: 'view',
  users_permissions: 'none',
};

const teamCoordinator: TeamPermissionMatrix = {
  planning_board: 'none',
  season_planning: 'none',
  matches_lineups: 'view',
  team_events: 'edit',
  player_availability: 'edit',
  fees_payments: 'none',
  communications: 'edit',
  reports: 'view',
  roster_management: 'view',
  partner_preferences: 'none',
  users_permissions: 'none',
};

const viewer: TeamPermissionMatrix = {
  planning_board: 'view',
  season_planning: 'view',
  matches_lineups: 'view',
  team_events: 'view',
  player_availability: 'view',
  fees_payments: 'view',
  communications: 'view',
  reports: 'view',
  roster_management: 'view',
  partner_preferences: 'none',
  users_permissions: 'none',
};

const player: TeamPermissionMatrix = {
  planning_board: 'none',
  season_planning: 'none',
  matches_lineups: 'none',
  team_events: 'none',
  player_availability: 'none',
  fees_payments: 'none',
  communications: 'none',
  reports: 'none',
  roster_management: 'none',
  partner_preferences: 'none',
  users_permissions: 'none',
};

export const presetPermissionMatrix: Record<TeamPermissionPreset, TeamPermissionMatrix> = {
  admin,
  captain,
  co_captain: coCaptain,
  lineup_manager: lineupManager,
  team_coordinator: teamCoordinator,
  viewer,
  player,
  custom: viewer,
};

export function permissionColumn(feature: TeamPermissionFeature): keyof TeamCaptainAssignmentRecord {
  return `${feature}_access` as keyof TeamCaptainAssignmentRecord;
}

export function normalizeAccess(value: unknown, fallback: PermissionAccess = 'none'): PermissionAccess {
  return value === 'edit' || value === 'view' || value === 'none' ? value : fallback;
}

export function legacyAssignmentMatrix(assignment: Pick<TeamCaptainAssignmentRecord, 'assignment_role'>): TeamPermissionMatrix {
  if (assignment.assignment_role === 'captain') return captain;
  if (assignment.assignment_role === 'co_captain' || assignment.assignment_role === 'assistant_captain') return coCaptain;
  return viewer;
}

export function assignmentPermissionMatrix(assignment: TeamCaptainAssignmentRecord): TeamPermissionMatrix {
  const legacy = legacyAssignmentMatrix(assignment);
  return teamPermissionFeatures.reduce((matrix, feature) => {
    const value = assignment[permissionColumn(feature.key)];
    matrix[feature.key] = normalizeAccess(value, legacy[feature.key]);
    if (!feature.editable && matrix[feature.key] === 'edit') matrix[feature.key] = 'view';
    return matrix;
  }, {} as TeamPermissionMatrix);
}

export function accessRank(access: PermissionAccess): number {
  if (access === 'edit') return 2;
  if (access === 'view') return 1;
  return 0;
}

export function impliesView(access: PermissionAccess): boolean {
  return access === 'view' || access === 'edit';
}

export function detectPermissionPreset(matrix: TeamPermissionMatrix): TeamPermissionPreset {
  for (const preset of teamPermissionPresets.filter((item) => item.key !== 'custom')) {
    const presetMatrix = presetPermissionMatrix[preset.key];
    const matches = teamPermissionFeatures.every((feature) => matrix[feature.key] === presetMatrix[feature.key]);
    if (matches) return preset.key;
  }
  return 'custom';
}

export function matrixForPreset(preset: TeamPermissionPreset): TeamPermissionMatrix {
  return { ...presetPermissionMatrix[preset] };
}

export function matrixToAssignmentPatch(matrix: TeamPermissionMatrix, preset: TeamPermissionPreset) {
  return teamPermissionFeatures.reduce<Record<string, PermissionAccess | TeamPermissionPreset>>((patch, feature) => {
    patch[permissionColumn(feature.key)] = matrix[feature.key];
    return patch;
  }, { permission_preset: preset });
}

export function normalizePermissionPreset(value: unknown): TeamPermissionPreset {
  if (value === 'full_co_captain') return 'co_captain';
  if (value === 'view_only') return 'viewer';
  return teamPermissionPresets.some((preset) => preset.key === value) ? value as TeamPermissionPreset : 'custom';
}

export function permissionPresetLabel(preset: TeamPermissionPreset | 'workspace_owner' | null | undefined): string {
  if (preset === 'workspace_owner') return 'Workspace Owner';
  return teamPermissionPresets.find((item) => item.key === preset)?.label || 'Custom';
}

export function permissionPresetSummary(preset: TeamPermissionPreset): string {
  if (preset === 'admin') return 'All assignable team permissions';
  if (preset === 'captain') return 'Full team management';
  if (preset === 'co_captain') return 'Full team management';
  if (preset === 'lineup_manager') return 'Lineups and match planning';
  if (preset === 'team_coordinator') return 'Team operations';
  if (preset === 'viewer') return 'Read-only access';
  if (preset === 'player') return 'Account access with roster-derived team scope';
  return 'Custom permissions';
}
